import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';

import { AlertBlock, Link, List, SpacerHorizontal, Strong, Typography } from '@klarna/bubble-ui';
import SelectorExpandable from '@bubble-contrib/molecule-selector-expandable';
import { VerticalRhythm } from 'mage-components';
import { translator } from 'mage-i18n';

import {
  getSelectedPlatform,
  platformProductStepInstructionsTranslator,
} from '@/selectors/merchant';
import { platformStepInstructionsTranslator } from '@/selectors/platforms';

const hasTextInstructions = ({ instructions }) => instructions && instructions.length;
const hasProductTextInstructions = ({ productInstructions }) =>
  productInstructions && productInstructions.length;
const hasInstructions = platform => hasTextInstructions(platform);

const tPrefix = 'products-fe.pages.whats_next_page';

const spacePrepareText = (index, length) => text => {
  if (index > 0) {
    text = ` ${text}`;
  }
  if (index < length - 1) {
    text = `${text} `;
  }
  return text;
};

const styles = {
  titleWrapper: {
    display: 'flex',
  },
  listWrapper: {
    padding: '0 25px',
  },
};

// eslint-disable-next-line react/prop-types
const InstructionPart = ({ element, keyPrefix, preprocessText }) => {
  if (element instanceof Array) {
    return (
      <>
        {/* eslint-disable-next-line react/prop-types */}
        {element.map((instructionPart, index) => (
          <InstructionPart
            key={`${keyPrefix}parts-${index}`}
            element={instructionPart}
            keyPrefix={`${keyPrefix}parts-${index}-`}
            // eslint-disable-next-line react/prop-types
            preprocessText={spacePrepareText(index, element.length)}
          />
        ))}
      </>
    );
  }

  const { link, text, bold, list } = element; /* eslint-disable-line react/prop-types */

  if (link) {
    return (
      <Link key={`${keyPrefix}link`} href={link} target="_blank">
        {text}
      </Link>
    );
  }

  if (bold) {
    return <Strong key={`${keyPrefix}bold`}>{bold}</Strong>;
  }

  if (list) {
    const keyList = `${keyPrefix}list`;
    // eslint-disable-next-line react/prop-types
    const items = list.map((itemPart, index) => {
      return (
        <List.Item key={index}>
          <InstructionPart
            key={`${keyList}-item`}
            element={itemPart}
            keyPrefix={`${keyList}-item-${index}-`}
            // eslint-disable-next-line react/prop-types
            preprocessText={spacePrepareText(index, list.length)}
          />
        </List.Item>
      );
    });

    return <List key={keyList}>{items}</List>;
  }

  return preprocessText(text);
};

const Description = ({ instructions = [] }) => (
  <div style={styles.listWrapper}>
    <List>
      {instructions.map((step, index) => (
        <List.Item key={index}>
          <InstructionPart key={index} element={step} />
        </List.Item>
      ))}
    </List>
  </div>
);

Description.propTypes = { instructions: PropTypes.array };

const Instructions = ({ platform, fallbackInstructions, t }) => {
  let instructions = [];
  if (hasProductTextInstructions(platform)) {
    instructions = platform.productInstructions;
  } else if (hasTextInstructions(platform)) {
    instructions = platform.instructions;
  } else {
    instructions = fallbackInstructions;
  }

  const alternate = _get(instructions, '0.alternate');

  return (
    <>
      <Typography textToken="text-style/headings/blocks/bold/tertiary">
        {t(`${tPrefix}.ready_to_connect.content.title`)}
      </Typography>
      <VerticalRhythm spacing={6} />
      <SelectorExpandable
        id="integration-guide"
        options={instructions.map(({ title, stepInstructions }, index) => ({
          label: (
            <div style={styles.titleWrapper}>
              <Typography textToken="text-style/headings/blocks/bold/grande">
                {index + 1}
              </Typography>
              <SpacerHorizontal spaceToken="space/300" />
              <Typography textToken="text-style/headings/blocks/bold/grande">{title}</Typography>
            </div>
          ),
          description: <Description instructions={stepInstructions} />,
          expanded: true,
        }))}
      />
      {alternate && (
        <>
          <VerticalRhythm spacing={6} />
          <AlertBlock
            iconType={() => null}
            content={[<InstructionPart key={0} element={alternate} />]}
          />
        </>
      )}
    </>
  );
};

Instructions.propTypes = {
  t: PropTypes.func.isRequired,
  fallbackInstructions: PropTypes.array,
  platform: PropTypes.object,
};

export default connect(state => {
  const platform = getSelectedPlatform(state);
  platform.instructions = platformStepInstructionsTranslator(platform.id)(state);
  platform.productInstructions = platformProductStepInstructionsTranslator(platform.id)(state);
  let fallbackInstructions = [];
  if (!hasInstructions(platform)) {
    fallbackInstructions = platformStepInstructionsTranslator('no_instructions')(state);
  }

  return { platform, fallbackInstructions, t: translator(state) };
})(Instructions);
